import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { configs } from "configs";
import { getHeaders } from "store/parentApi";
import { convertDateTimePayload } from "utils/date";
import { inDevelopMode } from "utils/env";

const transformErrorResponse = (error) => {
  console.error("Request Error: ", error);
  return {
    ...error,
    status: error?.originalStatus || error?.status,
    message:
      error?.data?.errors?.[0]?.message ||
      (Array.isArray(error?.data?.errors)
        ? error.data.errors.join(", ")
        : error?.data?.errors) ||
      error?.data?.message?.reasons?.join("\n") ||
      error?.data?.message ||
      "Network error",
  };
};

// Transform login response to include auth headers in the data
const transformLoginResponse = (response, meta) => {
  const headers = meta?.response?.headers;
  return {
    ...response,
    authHeaders: {
      "access-token": headers?.get("access-token") || null,
      client: headers?.get("client") || null,
      uid: headers?.get("uid") || null,
    },
  };
};

// Custom base query that uses auth headers for authenticated endpoints
const authBaseQuery = async (args, api, extraOptions) => {
  // Check if this is an authenticated request (logout, changePassword, etc.)
  const needsAuth = args?.url !== "/auth/sign_in" && 
                    !args?.url?.startsWith("/auth/google_login");
  
  const baseQuery = fetchBaseQuery({
    baseUrl: configs.apiBaseURL()?.replace(/\/api$/, ""),
    credentials: inDevelopMode ? undefined : "include",
    ...(needsAuth ? getHeaders() : {}),
  });
  
  return baseQuery(args, api, extraOptions);
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: authBaseQuery,

  endpoints: (builder) => ({
    // Email/Password Login - POST /auth/sign_in
    signIn: builder.mutation({
      query: ({ email, password }) => {
        return {
        url: "/auth/sign_in",
        method: "POST",
        body: { email, password },
      }},
      transformResponse: transformLoginResponse,
      transformErrorResponse,
    }),

    // Google OAuth Login - POST /auth/google_login?access_token=<token>
    googleLogin: builder.mutation({
      query: ({ accessToken }) => ({
        url: `/auth/google_login?access_token=${accessToken}`,
        method: "POST",
      }),
      transformResponse: transformLoginResponse,
      transformErrorResponse,
    }),

    logout: builder.mutation({
      query: () => ({
        url: "/auth/sign_out",
        method: "Delete",
      }),
      transformErrorResponse,
    }),

    changePassword: builder.mutation({
      query: ({ payload }) => {
        let body = {
          password: payload?.newPassword,
          current_password: payload?.currentPassword,
          password_confirmation: payload?.confirmPassword,
        };
        return { url: `/auth/password`, method: "PUT", body: body };
      },
    }),

    addStaff: builder.mutation({
      query: (payload) => {
        let body = {
          email: payload?.email,
          employee_id: payload?.employeeId,
          primary_contact: payload?.officialNumber,
          ...(payload?.joiningDate && { joining_date: convertDateTimePayload(payload?.joiningDate) }),
          name: payload?.name,
          ...(payload?.nationality && {
            nationality_id: parseInt(payload?.nationality),
          }),
          nic: payload?.nationalId,
          date_of_birth: convertDateTimePayload(payload?.dateOfBirth),
          language_id: parseInt(payload?.nativeLanguage),
          gender: payload?.gender,
          personal_contact_number: payload?.personalPhoneNumber,
          department_id: parseInt(payload?.department),
          ...(payload?.city && { city_id: parseInt(payload?.city) }),
          ...(payload?.state && { state_id: parseInt(payload?.state) }),
          country_id: parseInt(payload?.country),
          hierarchy_attributes: {
            designation_id: parseInt(payload?.designation),
            report_to_id: parseInt(payload?.lineManager?.id),
          },
        };

        return {
          url: `/auth`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["staff-listing"] : []; // ??
      },
      transformErrorResponse,
    }),
  }),
});

export const {
  useSignInMutation,
  useGoogleLoginMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useAddStaffMutation,
} = authApi;
